// eslint-disable-next-line no-unused-vars
import React, { memo, useCallback, useEffect, useState } from 'react';
import { useContextSelector } from 'use-context-selector';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';

import download from 'Utils/download';
import useRouter from 'Hooks/useRouter';
import useFetch from 'Hooks/useFetch';
import useToastNotifications from 'Hooks/useToastNotifications';
import { API_PRODUCTS_SEARCH, API_WISHLISTS_EXPORT } from 'Constants';
import WishlistsComponent from 'Components/Wishlists/Wishlists';
import { stockAlertWishlistId, WishlistContext } from 'Contexts/WishlistContext';
import { UserContext } from 'Contexts/UserContext';
import { ProductsContext } from 'Contexts/ProductsContext';
import { CartContext } from 'Contexts/CartContext';
import useAnalytics from 'Hooks/useAnalytics';
import useModals from 'Hooks/useModals';

const Wishlists = memo(() => {
  const { t } = useTranslation();
  const fetcher = useFetch();
  const { push, params } = useRouter();
  const analytics = useAnalytics();
  const modalContext = useModals();
  const updateProducts = useContextSelector(
    ProductsContext,
    data => data?.updateProducts
  );
  const user = useContextSelector(
    UserContext,
    data => data?.data || {}
  );
  const { data: cart, updateProducts: updateCartProducts } = useContextSelector(
    CartContext,
    data => data
  );
  const { getList, wishlists, updateProduct, deleteList } = useContextSelector(
    WishlistContext,
    data => {
      const lists = { ...(data?.data || {}) };
      if (data?.stockAlertWishlist) {
        lists[stockAlertWishlistId] = data?.stockAlertWishlist;
      }
      return {
        wishlists: lists,
        getList: data?.getList,
        updateProduct: data?.updateProduct,
        deleteList: data?.deleteList,
      };
    }
  );
  const toast = useToastNotifications();
  const [newProductSearch, setNewSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loadingSearchResults, setLoadingSearchResults] = useState(false);
  const [addToCartLoading, setAddToCartLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [existingProductSearch, setExistingSearch] = useState('');
  const [quantities, setQuantities] = useState({});
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [listItems, setListItems] = useState([]);
  const [showExistingProductsSearch, setShowExistingProductsSearch] = useState(false);

  const getListWrapper = useCallback(
    debounce(
      async (listId, userDefaultCurrency, userWithVat) => {
        if (!listId) return;
        try {
          setLoading(true);
          const wishlist = await getList(listId, userDefaultCurrency, userWithVat);
          if (wishlist) {
            setLoading(false);
            setList(wishlist);
            setListItems(wishlist.products);
          }
        } catch (error) {
          setLoading(false);
          if (error?.status !== 404) {
            toast.addNotification({
              type: 'error',
              title: t('wishlists.errors.errorAccessingWishlist'),
              subtitle: `List: ${listId}`
            });
          }
        }
      }, 250, { leading: true, trailing: true }
    ),
    []
  );

  useEffect(() => {
    if (
      list?.id in (wishlists || {})
      && list !== wishlists[list.id]
    ) {
      setList(wishlists[list.id]);
      setListItems(wishlists[list.id]?.products || []);
    }
  }, [wishlists]);

  useEffect(() => {
    if (selectedProduct) {
      setSearchOpen(false);
    }
  }, [selectedProduct]);

  useEffect(() => {
    if (
      user?.loggedIn
      && user?.email
    ) {
      getListWrapper(list?.id || params.id, user?.defaultCurrency, user?.withVat);
    }
  }, [
    user?.loggedIn,
    user?.defaultCurrency,
    user?.withVat
  ]);

  useEffect(() => {
    if (params.id) {
      getListWrapper(params.id, user?.defaultCurrency, user?.withVat);
    }
  }, [params.id]);

  useEffect(() => {
    if (!list) {
      setExistingSearch('');
      setQuantities({});
    }
  }, [list]);

  useEffect(() => {
    if (listItems) {
      setQuantities(listItems.reduce((acc, item) => {
        acc[item.id] = {
          quantity: item.quantity,
          stockValue: item.stockValue,
          minQuantity: item.minQuantity
        };
        return acc;
      }, {}));
    }
  }, [listItems]);

  const handleOnKeyDown = e => {
    if (e.keyCode === 13) {
      addProductToList(e);
    }
  };

  const addProductToList = async e => {
    analytics.track('added_product_to_wishlist', {
      product_id: selectedProduct?.id,
      product_sku: selectedProduct?.code,
    });
    e.preventDefault();
    const inputEl = e.target.parentElement.querySelector('input');
    const formEl = e.target.closest('form');
    if (!inputEl.value.trim().length) {
      inputEl.setCustomValidity('');
      formEl.reportValidity();
      return;
    }
    if (selectedProduct) {
      try {
        setSearchOpen(false);
        setLoading(true);
        await updateProduct(list.id, {
          ...selectedProduct,
          quantity: selectedProduct.quantity || selectedProduct.minQuantity || 1
        });
        toast.addNotification({
          title: t('wishlist.successfullyAddedToWishlist')
        });
        getListWrapper(list.id, user?.defaultCurrency, user?.withVat);
      } catch (error) {
        toast.addNotification({
          type: 'error',
          title: error?.info?.errors?.at(0).message || t('wishlist.failedUpdatingWishlist')
        });
      } finally {
        setLoading(false);
        setExistingSearch('');
        setNewSearch('');
        setSelectedProduct(null);
        setSearchResults([]);
      }
    } else {
      inputEl.setCustomValidity(
        t('wishlists.pleaseSelectProduct')
      );
      formEl.reportValidity();
    }
  };

  const debouncedNewProductSearch = useCallback(
    debounce(async search => {
      if (
        search.length < 3
        || search === selectedProduct?.title
      ) return;
      setSearchOpen(true);
      setLoadingSearchResults(true);
      const searchString = `${ API_PRODUCTS_SEARCH }?search=${ search }`;
      try {
        const searchedProducts = await fetcher(searchString);
        if (searchedProducts) {
          if (searchedProducts?.products?.length) {
            setSearchResults(searchedProducts.products);
          }
          setLoadingSearchResults(false);
        }
      } catch (error) {
        // nothing for now
      }
    }, 250, { leading: false, trailing: true }),
    [selectedProduct]
  );

  const debouncedExistingProductSearch = useCallback(
    debounce(value => {
      if (value.trim().length) {
        const re = new RegExp(value.trim(), 'ig');
        setListItems(() => (list?.products || [])
          .filter(product => re.test(product.code) || re.test(product.name))
        );
      } else {
        setListItems(list?.products || []);
      }
    }, 250, { leading: false, trailing: true }),
    [list]
  );

  const handleOnAddToCart = async () => {
    analytics.track('added_wishlist_to_cart');
    const productsToAdd = (listItems || []).reduce((acc, product) => {
      if (
        !product.stockAvailable
        || !(product.id in (quantities || {}))
        || !quantities[product.id]
        || quantities[product.id].quantity < product.minQuantity
      ) return acc;

      const qty = quantities[product.id].quantity;
      const existingCartItem = (cart?.items || [])
        .find(item => (item.id || item.productId) === product.id);
      if (existingCartItem) {
        acc.push({
          ...existingCartItem,
          ...product,
          quantity: Math.min(
            product.stockValue,
            Math.max(product.minQuantity || 1, existingCartItem.quantity + qty)
          )
        });
      } else {
        acc.push(({
          ...product,
          quantity: qty
        }));
      }
      return acc;
    }, []);
    if (!productsToAdd.length) return;

    setAddToCartLoading(true);
    try {
      await updateCartProducts(productsToAdd);
      updateProducts(productsToAdd.map(p => ({ ...p, quantity: 0 })));
    } catch (error) {
      toast.addNotification({
        type: 'error',
        title: t('cart.failedAddingProductsToCart')
      });
    }
    setAddToCartLoading(false);
  };

  const handleOnToggleSearch = useCallback(
    state => () => {
      analytics.track('toggle_search');
      if (state) {
        if (newProductSearch.trim().length >= 3) {
          setSearchOpen(state);
        }
      } else {
        setSearchOpen(state);
      }
    },
    [newProductSearch]
  );

  const handleOnChangeQty = prod => {
    if (prod.quantity === 0) {
      setList(prevList => {
        if ('products' in prevList) {
          return {
            ...prevList,
            products: prevList.products.filter(p => p.id !== prod.id)
          };
        }
        return prevList;
      });

      setListItems(prevItems => prevItems.filter(p => p.id !== prod.id));
    }

    setQuantities(prevQty => {
      const product = prevQty[prod.id];
      return {
        ...prevQty,
        [prod.id]: {
          ...(product || {}),
          quantity: prod.quantity
        }
      };
    });
  };

  const handleOnClearNewInputSearch = () => {
    analytics.track('clear_search');
    setSelectedProduct(null);
    setExistingSearch('');
    setSearchOpen(false);
    setSearchResults([]);
  };

  const handleOnChangeInputValue = inputKey => e => {
    const { value } = e.target;
    if (inputKey === 'new') {
      setNewSearch(value);
      debouncedNewProductSearch(value);
    } else {
      setExistingSearch(value);
      debouncedExistingProductSearch(value);
    }
  };

  const handleOnChangeList = listId => {
    push(`/lists/${listId}`);
  };

  const handleOnDeleteList = async () => {
    analytics.track('deleted_wishlist');
    if (list.isDefault) {
      toast.addNotification({
        type: 'warning',
        title: t('wishlists.error.defaultCantDelete')
      });
      return;
    }
    await deleteList(list.id);
    toast.addNotification({
      type: 'warning',
      title: t('wishlists.deletedWishlist')
    });
    const newDefaultList = Object.values(wishlists).find(item => item.isDefault);
    if (newDefaultList) {
      handleOnChangeList(newDefaultList.id);
    }
  };

  const handleWishlistDownload = () => {
    analytics.track('downloaded_wishlist');
    download(`${API_WISHLISTS_EXPORT}/${list.id}`);
  };

  const handleGenerateOffer = () => {
    analytics.track('created_offer_from_wishlist', {
      wishlist_id: list?.id || ''
    });
    modalContext.setState(modalContext.modalState.ADD_TO_WISHLIST.key, {
      product: null,
      wishlist: list,
      action: '',
      mode: 'createOffer',
      open: true
    });
  };

  useEffect(() => {
    if (!params.id && Object.keys(wishlists || {}).length > 0) {
      const defaultList = Object.values(wishlists).find(item => item.isDefault);
      const listId = defaultList?.id || Object.values(wishlists)?.at(0)?.id;
      if (listId) {
        push(`/lists/${listId}`);
      }
    }
  }, [params.id, wishlists]);

  return (
    <WishlistsComponent
      onDeleteList={ handleOnDeleteList }
      onChangeList={ handleOnChangeList }
      onClear={ handleOnClearNewInputSearch }
      selectProduct={ setSelectedProduct }
      selectedProduct={ selectedProduct }
      onAddProduct={ addProductToList }
      onKeyDown={ handleOnKeyDown }
      onChangeInputValue={ handleOnChangeInputValue }
      onWishlistDownload={ handleWishlistDownload }
      onGenerateOffer={ handleGenerateOffer }
      searchResults={ searchResults }
      loadingSearch={ loadingSearchResults }
      existingProductSearch={ existingProductSearch }
      wishlist={ list }
      products={ listItems }
      loading={ loading }
      searchOpen={ searchOpen }
      onAddToCart={ handleOnAddToCart }
      onChangeQty={ handleOnChangeQty }
      addToCartLoading={ addToCartLoading }
      quantities={ quantities }
      onToggleSearch={ handleOnToggleSearch }
      newProductSearch={ newProductSearch }
      showExistingProductsSearch={ showExistingProductsSearch }
      setShowExistingProductsSearch={ setShowExistingProductsSearch }
    />
  );
});

Wishlists.displayName = 'WishlistsContainer';

export default Wishlists;
